import dayjs from 'dayjs'
import isoWeek from 'dayjs/plugin/isoWeek'
import { getCssVar } from 'quasar'
import { reactive } from 'vue'

import { daySpanViewLabel } from '@/composables/date-range-presets'
import { getI18n } from '@/plugins/i18n'
import { stringToColor } from '@/lib/utils'
import { usePlanningStore, VACATION_PROJECT_ID } from '@/stores/planning.store'
import { usePortalStore } from '@/stores/portal.store'
import {
  ABSENCE_COLOR,
  calendarViewEnum,
  eventTypeEnum,
  PROJECT_FALLBACK_COLOR,
  useScheduleStore,
} from '@/stores/schedule.store'

dayjs.extend(isoWeek)

export const MINUTES_PER_HOUR = 60
export const MINUTES_PER_DAY = 1440
export const DAYS_PER_WEEK = 7
export const SCHEDULE_CHUNK_DAYS = 42
const MIN_VISUAL_MINUTES = 20
const COLUMN_OVERLAP_FACTOR = 1.7

export const calendarViewDefinitions = Object.freeze([
  { value: calendarViewEnum.DAY, days: 1, icon: 'mdi-view-day-outline', shortcut: 'd', label: t => t('common.day') },
  { value: calendarViewEnum.THREE_DAYS, days: 3, icon: 'mdi-view-column-outline', shortcut: 'x', label: t => daySpanViewLabel(t, 3) },
  { value: calendarViewEnum.WORK_WEEK, days: 5, weekAligned: true, icon: 'mdi-calendar-week-begin-outline', shortcut: 'e', label: t => daySpanViewLabel(t, 5) },
  { value: calendarViewEnum.WEEK, days: DAYS_PER_WEEK, weekAligned: true, icon: 'mdi-view-week-outline', shortcut: 'w', label: t => daySpanViewLabel(t, DAYS_PER_WEEK) },
  { value: calendarViewEnum.MONTH, icon: 'mdi-view-module-outline', shortcut: 'm', label: t => t('common.month') },
  { value: calendarViewEnum.SCHEDULE, icon: 'mdi-view-agenda-outline', shortcut: 'a', label: t => t('common.calendarSchedule') },
])

export const calendarViewDefinition = (view) =>
  calendarViewDefinitions.find(definition => definition.value === view) ??
  calendarViewDefinitions.find(definition => definition.value === calendarViewEnum.WEEK)

export const isTimeGridView = (view) => !!calendarViewDefinition(view).days

export const visibleRange = (view, date, scheduleDays = SCHEDULE_CHUNK_DAYS) => {
  const day = dayjs(date).startOf('day')
  if (view === calendarViewEnum.MONTH) {
    const start = day.startOf('month').startOf('isoWeek')
    return { start, end: day.endOf('month').endOf('isoWeek').add(1, 'day').startOf('day') }
  }
  if (view === calendarViewEnum.SCHEDULE) {
    const start = day.startOf('month')
    return { start, end: start.add(scheduleDays, 'day') }
  }
  const { days, weekAligned } = calendarViewDefinition(view)
  const start = weekAligned ? day.startOf('isoWeek') : day
  return { start, end: start.add(days, 'day') }
}

export const shiftDate = (view, date, direction) => {
  const day = dayjs(date)
  if (view === calendarViewEnum.MONTH || view === calendarViewEnum.SCHEDULE) {
    return day.add(direction, 'month').startOf('month')
  }
  const { days, weekAligned } = calendarViewDefinition(view)
  return weekAligned ? day.add(direction, 'week') : day.add(direction * days, 'day')
}

export const daysBetween = (start, end) => {
  const days = []
  for (let day = start; day.isBefore(end); day = day.add(1, 'day')) {
    days.push(day)
  }
  return days
}

export const dayKey = (day) => day.format('YYYY-MM-DD')

export const isAllDayEvent = (event) =>
  event.is_project_root || event.endAt.diff(event.startAt, 'minute') >= MINUTES_PER_DAY

export const eventSpan = (event, rangeStart, dayCount) => {
  const first = event.startAt.startOf('day').diff(rangeStart, 'day')
  const last = Math.max(first, event.endAt.subtract(1, 'ms').startOf('day').diff(rangeStart, 'day'))
  if (last < 0 || first >= dayCount) {
    return null
  }
  return {
    event,
    from: Math.max(first, 0),
    to: Math.min(last, dayCount - 1),
    clippedStart: first < 0,
    clippedEnd: last >= dayCount,
  }
}

const spanOrder = (a, b) =>
  a.from - b.from ||
  (b.to - b.from) - (a.to - a.from) ||
  Number(b.event.is_project_root) - Number(a.event.is_project_root) ||
  a.event.startAt.valueOf() - b.event.startAt.valueOf()

export const packLanes = (spans) => {
  const laneEnds = []
  const items = [...spans].sort(spanOrder).map(span => {
    let lane = laneEnds.findIndex(end => end < span.from)
    if (lane === -1) {
      lane = laneEnds.length
    }
    laneEnds[lane] = span.to
    return { ...span, lane }
  })
  return { items, laneCount: laneEnds.length }
}

const overlaps = (a, b) => a.from < b.extent && b.from < a.extent

const layoutColumns = (segments) => {
  const laidOut = []
  let cluster = []
  let columns = []
  let clusterEnd = -Infinity

  const flush = () => {
    cluster.forEach(item => {
      const remaining = columns.length - item.column
      let span = 1
      while (
        item.column + span < columns.length &&
        !columns[item.column + span].some(other => overlaps(other, item))
      ) {
        span++
      }
      const blocked = span < remaining
      laidOut.push({
        ...item,
        left: item.column / columns.length,
        width: (blocked ? Math.min(remaining, span * COLUMN_OVERLAP_FACTOR) : remaining) / columns.length,
        layer: item.column + 1,
      })
    })
    cluster = []
    columns = []
  }

  ;[...segments]
    .sort((a, b) => a.from - b.from || b.extent - a.extent)
    .forEach(segment => {
      if (segment.from >= clusterEnd) {
        flush()
        clusterEnd = -Infinity
      }
      let column = columns.findIndex(placed => placed[placed.length - 1].extent <= segment.from)
      if (column === -1) {
        column = columns.length
        columns.push([])
      }
      columns[column].push(segment)
      cluster.push({ ...segment, column })
      clusterEnd = Math.max(clusterEnd, segment.extent)
    })
  flush()
  return laidOut
}

export const timedSegments = (events, day, startMinute, endMinute) => {
  const dayStart = day.startOf('day')
  const segments = events.flatMap(event => {
    const start = event.startAt.diff(dayStart, 'minute')
    const end = event.endAt.diff(dayStart, 'minute')
    const from = Math.max(start, startMinute)
    const to = Math.min(end, endMinute)
    if (to <= from) {
      return []
    }
    return [{
      event,
      from,
      to,
      extent: Math.min(endMinute, Math.max(to, from + MIN_VISUAL_MINUTES)),
      clippedStart: start < startMinute,
      clippedEnd: end > endMinute,
    }]
  })
  return layoutColumns(segments)
}

export const minuteOfDay = (date) => date.hour() * MINUTES_PER_HOUR + date.minute()

export const useCalendarFormatters = (locale) => {
  const formatter = (options) => new Intl.DateTimeFormat(locale, options)
  return {
    weekday: formatter({ weekday: 'short' }),
    weekdayNarrow: formatter({ weekday: 'narrow' }),
    dayLong: formatter({ weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }),
    dayMonth: formatter({ day: 'numeric', month: 'short' }),
    monthShort: formatter({ month: 'short' }),
    monthLong: formatter({ month: 'long' }),
    monthYear: formatter({ month: 'long', year: 'numeric' }),
    monthShortYear: formatter({ month: 'short', year: 'numeric' }),
    time: formatter({ hour: '2-digit', minute: '2-digit' }),
    timeZone: formatter({ timeZoneName: 'short' }),
  }
}

export const formatDuration = (start, end) => {
  const minutes = end.diff(start, 'minute')
  const hours = Math.floor(minutes / MINUTES_PER_HOUR)
  const rest = minutes % MINUTES_PER_HOUR
  return rest ? `${hours}h${String(rest).padStart(2, '0')}` : `${hours}h`
}

export const formatTimeRange = (formatters, start, end) =>
  `${formatters.time.format(start.toDate())} – ${formatters.time.format(end.toDate())}`

export const timeZoneLabel = (formatters) =>
  formatters.timeZone.formatToParts(new Date()).find(part => part.type === 'timeZoneName')?.value ?? ''

export const resourceColor = (service) => (service?.name ? stringToColor(service.name) : PROJECT_FALLBACK_COLOR)

export function useEventCalendarData() {
  const scheduleStore = useScheduleStore()
  const planningStore = usePlanningStore()
  const portalStore = usePortalStore()

  const state = reactive({ events: [], loading: false })
  let lastRequest = 0

  const statusLabel = (event, project, projectStatuses) => {
    const { t } = getI18n()
    if (event.type === eventTypeEnum.PROJECT_ROOT) {
      return projectStatuses[project?.status]?.name ?? ''
    }
    if (event.type === eventTypeEnum.ABSENCE) {
      return t(event.confirmed ? 'common.approved' : 'common.pendingApproval')
    }
    return t(event.confirmed ? 'common.confirmed' : 'common.notConfirmed')
  }

  const toCalendarEvents = (rows) => {
    const projectStatuses = planningStore.getLocalizedProjectStatuses()
    const projectsById = new Map(planningStore.projects.map(project => [project.id, project]))
    const staffById = new Map(planningStore.staff.map(staff => [staff.id, staff]))
    const equipmentById = new Map(planningStore.equipments.map(equipment => [equipment.id, equipment]))
    const vacationProject = planningStore.getVacationProject()

    return rows
      .map(row => {
        const isAbsence = row.type === eventTypeEnum.ABSENCE
        const project = isAbsence ? vacationProject : projectsById.get(row.project_id)
        const staff = staffById.get(row.staff_id)
        const equipment = equipmentById.get(row.equipment_id)
        const service = staff || equipment
        const isProjectRoot = row.type === eventTypeEnum.PROJECT_ROOT
        return {
          ...row,
          is_project_root: isProjectRoot,
          project_id: row.project_id ?? VACATION_PROJECT_ID,
          project,
          staff,
          equipment,
          startAt: dayjs(row.start),
          endAt: dayjs(row.end),
          color: isAbsence ? ABSENCE_COLOR : project?.color || (isProjectRoot ? PROJECT_FALLBACK_COLOR : getCssVar('primary')),
          resourceColor: resourceColor(service),
          title: isProjectRoot ? project?.name : planningStore.getServiceName(service, true),
          subtitle: isProjectRoot || isAbsence ? '' : project?.name,
          status: statusLabel(row, project, projectStatuses),
        }
      })
      .filter(event => event.endAt.isAfter(event.startAt))
  }

  const loadAsync = async({ start, end, includeAllocations, includeAbsences }) => {
    const request = ++lastRequest
    state.loading = true
    const params = {
      start: start.toISOString(),
      end: end.toISOString(),
      include_project_roots: true,
      ...(includeAbsences && { include_absences: true }),
    }
    // The endpoint scopes allocations by one service type at a time; an empty service list
    // scopes out every allocation, leaving project roots and absences.
    const scopes = includeAllocations
      ? [portalStore.serviceTypeEnum.STAFF, portalStore.serviceTypeEnum.EQUIPMENT].map(serviceType => ({ ...params, service_type: serviceType }))
      : [{ ...params, service_ids: '' }]
    try {
      const responses = await Promise.all(scopes.map(scope => scheduleStore.fetchEventsAsync(scope)))
      if (request === lastRequest) {
        state.events = toCalendarEvents([...new Map(responses.flatMap(({ data }) => data).map(row => [row.id, row])).values()])
      }
    } finally {
      if (request === lastRequest) {
        state.loading = false
      }
    }
  }

  return { state, loadAsync }
}

export const MIN_HOUR_HEIGHT = 1.5
export const MAX_HOUR_HEIGHT = 8

export const clampHourHeight = (height) => Math.min(MAX_HOUR_HEIGHT, Math.max(MIN_HOUR_HEIGHT, height))

export const eventClasses = (event) => ({
  'cal-event--project': event.is_project_root,
  'cal-event--unconfirmed': !event.is_project_root && !event.confirmed,
})
